import { useEffect, useRef, useState } from "react";
import type { AuthorityCurrency, InstructionView, PersonCard, PlayerAction, RequestView } from "../types";

export const CURRENCIES: { id: AuthorityCurrency; label: string; hint: string }[] = [
  { id: "spend", label: "Spend", hint: "can spend money on it without asking" },
  { id: "direct", label: "Direct", hint: "can tell others what to do about it" },
  { id: "prioritise", label: "Prioritise", hint: "can decide what comes first" },
  { id: "impose", label: "Impose", hint: "can set deadlines others must meet, partners included" },
];

/**
 * Standing instructions (08 §3, 19 §4.3): "When this comes up, it goes to [person], with [authority]".
 * Requests are described in the player's language; no quality score is shown.
 */
export function InstructionsPanel({ requests, instructions, people, busy, onAct, you }: {
  requests: RequestView[]; instructions: InstructionView[]; people: PersonCard[]; busy: boolean;
  onAct: (a: PlayerAction) => void;
  /** Who you are, and the authority you hold: you can only hand over what you have. */
  you: { id: string; authority: Record<AuthorityCurrency, "none" | "some" | "full"> };
}) {
  const colleagues = people.filter((p) => p.id !== you.id && p.availability === "in");
  return (
    <section className="panel" aria-labelledby="instr-title">
      <h2 id="instr-title" className="panel-title">Standing instructions</h2>
      {requests.length === 0 ? (
        <p className="panel-note">Requests that keep coming to you will be listed here once you have answered them.</p>
      ) : (
        <ul className="instr-list">
          {requests.map((r) => (
            <InstructionRow key={r.signature} request={r} current={instructions.find((i) => i.signature === r.signature)}
              colleagues={colleagues} busy={busy} onAct={onAct} authority={you.authority} />
          ))}
        </ul>
      )}
    </section>
  );
}

function InstructionRow({ request, current, colleagues, busy, onAct, authority }: {
  request: RequestView; current: InstructionView | undefined; colleagues: PersonCard[]; busy: boolean;
  onAct: (a: PlayerAction) => void; authority: Record<AuthorityCurrency, "none" | "some" | "full">;
}) {
  const [personId, setPersonId] = useState(current?.person.id ?? "");
  const [currencies, setCurrencies] = useState<AuthorityCurrency[]>((current?.currencies ?? []) as AuthorityCurrency[]);
  const [editing, setEditing] = useState(false);
  const idBase = `instr-${request.signature.replace(/[^a-z0-9_-]/gi, "")}`;
  const heading = useRef<HTMLHeadingElement>(null);
  const select = useRef<HTMLSelectElement>(null);
  /** Where focus goes after the form or the summary is swapped for the other (WCAG 2.4.3). */
  const after = useRef<"heading" | "select" | null>(null);
  useEffect(() => {
    if (after.current === "select") select.current?.focus();
    else if (after.current === "heading") heading.current?.focus();
    after.current = null;
  });
  const toggle = (c: AuthorityCurrency) => setCurrencies((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);

  return (
    <li className="instr">
      <div className="instr__head">
        <h3 className="instr__label" id={`${idBase}-label`} ref={heading} tabIndex={-1}>{request.label}</h3>
        {request.count4w !== null && <span className="instr__count">{request.count4w}× in the last four weeks</span>}
      </div>
      {request.detail && <p className="instr__detail">{request.detail}</p>}
      {request.example && request.example !== request.label && <p className="instr__example muted">First came to you as “{request.example}”.</p>}
      {current && !editing ? (
        <div className="instr__current">
          <p>
            Goes to <strong>{current.person.name}</strong>
            {current.currencies.length > 0
              ? <>, who can {current.currencies.map((c) => CURRENCIES.find((x) => x.id === c)?.label.toLowerCase() ?? c).join(", ")}</>
              : <>, with no particular authority</>}
            .
          </p>
          <div className="row-actions">
            <button type="button" className="btn" onClick={() => { after.current = "select"; setEditing(true); }} disabled={busy}
              aria-describedby={`${idBase}-label`}>Change</button>
            <button type="button" className="btn btn--quiet" disabled={busy} aria-describedby={`${idBase}-label`}
              onClick={() => { after.current = "heading"; onAct({ type: "revokeInstruction", signature: request.signature }); }}>Stop this instruction</button>
          </div>
        </div>
      ) : (
        <form className="instr__form" onSubmit={(e) => {
          e.preventDefault();
          if (!personId) return;
          onAct({ type: "instruct", signature: request.signature, personId, currencies });
          after.current = "heading";
          setEditing(false);
        }}>
          <label htmlFor={`${idBase}-person`}>When this comes up, it goes to</label>
          <select id={`${idBase}-person`} ref={select} value={personId} onChange={(e) => setPersonId(e.target.value)} required>
            <option value="">Choose someone…</option>
            {colleagues.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
          </select>
          <fieldset className="instr__currencies">
            <legend>With your authority to</legend>
            {CURRENCIES.map((c) => (
              <label key={c.id} className="check">
                <input type="checkbox" checked={currencies.includes(c.id)} onChange={() => toggle(c.id)} disabled={authority[c.id] === "none"} />
                <span><strong>{c.label}</strong> <span className="muted">— {authority[c.id] === "none" ? "you don't hold this authority yourself, so you can't hand it over" : c.hint}</span></span>
              </label>
            ))}
          </fieldset>
          <div className="row-actions">
            <button type="submit" className="btn btn--primary" disabled={busy || !personId}>Give this instruction</button>
            {current && <button type="button" className="btn btn--quiet" onClick={() => { after.current = "heading"; setEditing(false); }}>Cancel</button>}
          </div>
        </form>
      )}
    </li>
  );
}
